import { useMutation } from '@apollo/react-hooks';
import { useAccount } from '@bnnvara/accounts';
import { Bell as BellIcon } from '@bnnvara/icons/src/components/UserInterfaceIcons/Bell';
import { useOnOutsideClick, usePrevious } from '@bnnvara/react-hooks';
import React, { useRef } from 'react';

import { MarkAllNotificationsAsRead } from '../../../../api/mutations/notifications.gql';
import { useNotifications } from '../../../../hooks/notifications';
import useUI, {
  TYPE_TOGGLE_NOTIFICATIONS_DROPDOWN,
} from '../../../../hooks/useUI';
import { NotificationsDropdown } from './NotificationsDropdown';
import * as S from './styled';

const NotificationsHub = () => {
  const {
    isLoadingNotificationsHistory,
    setNotificationsHistory,
    notificationsHistory,
    amountOfUnreadNotifications,
  } = useNotifications();
  const { profile } = useAccount();
  const UID = profile && profile.UID;
  const {
    state: { notificationsDropdownOpen },
    dispatch,
  } = useUI();
  const showNumberOfNotifications = amountOfUnreadNotifications !== 0;
  const [markAllNotificationsAsReadMutation] = useMutation(
    MarkAllNotificationsAsRead
  );
  const prevHistory = usePrevious(notificationsHistory);

  /**
   * Put notification on read when a user click on hub and on a dropdownItem
   * Why dropdownItem? It is possible that the dropdown is open,
   * and that on that time a user receives a notification trough the websocket.
   */
  const setNotificationsOnRead = async () => {
    /**
     * Don't execute the mutation if there are 0 unread notifications,
     * to prevent unnecessary load in BFF and BE.
     * Also only execute if de dropDown is open, to assure
     * the dots (read or not read) of individual notifications is visible.
     */
    if (amountOfUnreadNotifications > 0 && notificationsDropdownOpen) {
      setNotificationsHistory((prevState) =>
        prevState.map((item) => ({ ...item, isRead: true }))
      );

      const result = await markAllNotificationsAsReadMutation({
        variables: { UID },
      });

      /**
       * In case the query is not executed successful the previous state is retrieved.
       * Apollo's build in optimistic response is not used for this,
       * because the notifications are not only retrieved with apollo,
       * but also with direct AWS websocket connection.
       */
      if (result.data.markAllNotificationsAsRead !== true) {
        setNotificationsHistory(prevHistory);
      }
    }
  };

  const refDropdownContainer = useRef();
  useOnOutsideClick(refDropdownContainer, () => {
    if (notificationsDropdownOpen) {
      setNotificationsOnRead();
      dispatch({ type: TYPE_TOGGLE_NOTIFICATIONS_DROPDOWN });
    }
  });

  return (
    <div ref={refDropdownContainer}>
      <S.Wrapper>
        <S.IconButton
          onClick={() => {
            if (notificationsDropdownOpen) {
              setNotificationsOnRead();
            }
            dispatch({ type: TYPE_TOGGLE_NOTIFICATIONS_DROPDOWN });
          }}
          isDropdownOpen={notificationsDropdownOpen}
        >
          <BellIcon />
          {showNumberOfNotifications && (
            <S.NumberOfNotifications>
              {amountOfUnreadNotifications}
            </S.NumberOfNotifications>
          )}
        </S.IconButton>
        {notificationsDropdownOpen && (
          <NotificationsDropdown
            isLoading={isLoadingNotificationsHistory}
            notifications={notificationsHistory}
            setNotificationsOnRead={setNotificationsOnRead}
          />
        )}
      </S.Wrapper>
    </div>
  );
};

export default NotificationsHub;
